import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../lib/appCommon";
import RealTradePreviewModal from "./RealTradePreviewModal";
import "./FlowPanel.css";

// ─── Flow Panel (Yahoo/Public.com-based options flow anomaly detector) ─────
// Real data throughout — /api/flow/tape, /api/flow/stats, /stream/flow SSE,
// /api/whales/congress — nothing simulated. A sentiment bar (real
// bullish/bearish counts from /flow/stats drawn as one proportion),
// type/direction filter chips, and the single highest-tier real event
// pulled out as a featured card so it doesn't get lost in the grid. Whale
// Tracking is a card list rather than a bare table, matching the Options
// Flow tab's visual language. React keys are deduped with a render-index
// suffix so same-millisecond SSE events never collide.
function FlowPanel() {
  const [flowEvents, setFlowEvents] = useState([]);
  const [connected, setConnected] = useState(false);
  const flowTickRef = useRef(0);
  const [whales, setWhales] = useState([]);
  const [whaleLoading, setWhaleLoading] = useState(false);
  const [flowTab, setFlowTab] = useState("flow");
  const [flowStats, setFlowStats] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all"); // all | sweep | volume_spike | cp_ratio
  const [dirFilter, setDirFilter] = useState("all"); // all | bullish | bearish

  const refreshStats = () => {
    axios.get(`${API}/flow/stats`).then(r => setFlowStats(r.data)).catch(() => {});
  };

  useEffect(() => {
    axios.get(`${API}/flow/tape?limit=50`).then(r => {
      if (Array.isArray(r.data)) setFlowEvents(r.data); // backend returns newest-first
    }).catch(() => {});
    refreshStats();

    // Same heartbeat/reconnect contract as DiamondUI.js/SwipeCards3.js/
    // FloorChat.js (45s heartbeat timeout, 4s onerror retry). Previously
    // tracked connected/disconnected on open/error but never actually forced
    // a reconnect or detected a zombie connection (still "open" but silently
    // dead — a real, known SSE failure mode) — the "Connecting..." label
    // could stay stuck forever with nothing driving a real retry beyond
    // whatever the browser's own native auto-reconnect happened to do.
    let cancelled = false, es, reconnect, heartbeat;
    const resetBeat = () => {
      clearTimeout(heartbeat);
      heartbeat = setTimeout(() => { if (cancelled) return; setConnected(false); es?.close(); reconnect = setTimeout(connect, 1000); }, 45000);
    };
    const connect = () => {
      if (cancelled) return;
      clearTimeout(reconnect); es?.close();
      es = new EventSource(`${API}/stream/flow?symbols=SPY,QQQ,AAPL,TSLA,NVDA,AAL`, { withCredentials: true });
      resetBeat();
      es.addEventListener("replay", e => {
        resetBeat();
        try {
          const events = JSON.parse(e.data);
          // SSE replay is oldest-first; prepend so newest stays at top
          if (Array.isArray(events)) setFlowEvents(prev => [...events.reverse(), ...prev].slice(0, 100));
        } catch {}
      });
      es.addEventListener("flow", e => {
        resetBeat();
        try {
          const parsed = JSON.parse(e.data);
          setFlowEvents(prev => [parsed, ...prev].slice(0, 100));
          // Refresh stats every 5 new events — counter lives outside the updater so it never triggers duplicate calls
          flowTickRef.current += 1;
          if (flowTickRef.current % 5 === 0) refreshStats();
        } catch {}
      });
      es.addEventListener("open", () => { setConnected(true); resetBeat(); });
      es.addEventListener("error", () => { setConnected(false); es.close(); clearTimeout(heartbeat); reconnect = setTimeout(connect, 4000); });
    };
    connect();
    return () => { cancelled = true; clearTimeout(reconnect); clearTimeout(heartbeat); es?.close(); };
  }, []);

  const [whaleMeta, setWhaleMeta] = useState(null); // {source, fetchedAt, symbol}
  const [whaleSymbol, setWhaleSymbol] = useState("");

  const loadWhales = async (sym) => {
    setWhaleLoading(true);
    try {
      const url = sym ? `${API}/whales/congress?symbol=${sym.toUpperCase()}` : `${API}/whales/congress`;
      const r = await axios.get(url);
      setWhales(r.data?.trades || []);
      setWhaleMeta({ source: r.data?.source, fetchedAt: r.data?.fetchedAt, symbol: r.data?.symbol });
    } catch {} finally { setWhaleLoading(false); }
  };

  useEffect(() => { loadWhales(); }, []);

  const [flowPopup, setFlowPopup] = useState(null); // the flow event whose popup is open
  const [flowBuyTrade, setFlowBuyTrade] = useState(null);

  // Generate plain-English explanation for any flow event
  function explainFlowEvent(ev) {
    const ticker = ev.ticker || "this ticker";
    const side = (ev.optionType || "").toUpperCase();
    const strike = ev.strike ? `$${ev.strike}` : "";
    const vol = ev.volume?.toLocaleString() || ev.callVolume?.toLocaleString() || null;
    const prem = ev.premium ? `$${(ev.premium / 1000).toFixed(0)}K` : null;
    const bias = ev.bias || "";
    const ratio = ev.ratio;

    if (ev.type === "sweep") {
      const dir = side === "CALL" ? "bullish" : side === "PUT" ? "bearish" : "directional";
      const action = side === "CALL" ? "rise above" : side === "PUT" ? "fall below" : "move past";
      return {
        headline: `Institutional Sweep — ${side || "Options"} ${side === "CALL" ? "🟢 Bullish" : side === "PUT" ? "🔴 Bearish" : "⚪ Neutral"}`,
        what: `A sweep hits multiple exchanges at the same time, meaning the buyer was too urgent to wait for a better price. That urgency is the signal — someone with big money made a ${dir} conviction bet.`,
        detail: `They bought ${vol ? vol + " " : ""}${ticker}${strike ? " " + strike : ""} ${side}s${prem ? " for " + prem + " in premium" : ""}. They need ${ticker} to ${action}${strike ? " " + strike : ""} before expiry to profit.`,
        signal: side === "CALL" ? "Bullish" : side === "PUT" ? "Bearish" : "Neutral",
        watch: `Watch for follow-through volume in ${ticker} over the next 1–3 sessions. If the stock starts moving in the same direction, institutional traders may know something.`,
      };
    }
    if (ev.type === "volume_spike") {
      const dir = side === "CALL" ? "bullish" : side === "PUT" ? "bearish" : "directional";
      return {
        headline: `Unusual Volume Spike — ${side ? side + "S" : "Options"} ${side === "CALL" ? "🟢 Bullish" : side === "PUT" ? "🔴 Bearish" : "⚪ Watch"}`,
        what: `${ticker} options volume just spiked well above the daily average. A spike on its own doesn't confirm direction, but combined with${prem ? " large premium (" + prem + ")" : " high dollar value"}, it usually means smart money is positioning.`,
        detail: `${vol ? vol + " contracts" : "High volume"} traded${strike ? " at the " + strike + " strike" : ""}. This is ${dir} positioning — traders expect ${ticker} to ${side === "PUT" ? "drop" : "rise"}.`,
        signal: side === "CALL" ? "Bullish" : side === "PUT" ? "Bearish" : "Watch",
        watch: `Check if there's pending news, earnings, or FDA event for ${ticker}. Volume spikes 1–5 days before catalysts are highly predictive.`,
      };
    }
    if (ev.type === "cp_ratio") {
      const isBullish = bias === "CALL HEAVY";
      return {
        headline: `Call/Put Ratio ${isBullish ? "🟢 CALL HEAVY — Bullish Sentiment" : bias === "PUT HEAVY" ? "🔴 PUT HEAVY — Bearish Hedge" : "⚪ Neutral Balance"}`,
        what: `The call/put ratio measures how many calls are being bought vs puts. ${ratio ? "A ratio of " + ratio + "x means calls are " + ratio + "x more popular than puts right now." : ""} ${isBullish ? "Traders are positioning for upside." : bias === "PUT HEAVY" ? "Traders are buying protection or betting on a drop." : "Activity is balanced — no strong directional bias."}`,
        detail: `This ratio covers ${ticker} option flow across the chain. ${prem ? "Total premium flow: " + prem + "." : ""} ${isBullish ? "Institutional call buying at this level often precedes upward price movement." : "Heavy put buying can signal a hedge against a large long position, or outright bearish speculation."}`,
        signal: isBullish ? "Bullish" : bias === "PUT HEAVY" ? "Bearish" : "Neutral",
        watch: `${isBullish ? "If " + ticker + " stock price confirms with a breakout, this becomes a high-conviction long signal." : "Monitor whether " + ticker + " breaks key support. If it does, the put buyers may have been right."}`,
      };
    }
    return {
      headline: `Options Flow Signal — ${ticker}`,
      what: "Unusual options activity detected. Large traders are positioning in this ticker.",
      detail: `${vol ? vol + " contracts" : "Volume"} traded${strike ? " at " + strike : ""}${prem ? " · " + prem + " premium" : ""}.`,
      signal: "Watch",
      watch: `Keep an eye on ${ticker} for follow-through price action.`,
    };
  }

  const TYPE_META = {
    volume_spike: { label: "VOL SPIKE", color: "#4ade80", bg: "rgba(74,222,128,0.12)", border: "rgba(74,222,128,0.3)" },
    sweep:        { label: "SWEEP",     color: "#a78bfa", bg: "rgba(167,139,250,0.12)", border: "rgba(167,139,250,0.3)" },
    cp_ratio:     { label: "C/P RATIO", color: "#60a5fa", bg: "rgba(96,165,250,0.12)",  border: "rgba(96,165,250,0.3)"  },
  };

  // ── Trade Tier Classification ──────────────────────────────────────────────
  const TIER_META = {
    diamond:  { icon: "💎", label: "DIAMOND",    color: "#67e8f9", bg: "rgba(103,232,249,0.10)", border: "rgba(103,232,249,0.45)", glow: "rgba(103,232,249,0.25)", desc: "Elite institutional move — massive conviction bet. These prints rarely lie." },
    gold:     { icon: "🥇", label: "GOLD",       color: "#fbbf24", bg: "rgba(251,191,36,0.10)",  border: "rgba(251,191,36,0.40)",  glow: "rgba(251,191,36,0.20)",  desc: "Strong smart-money signal — significant premium flow with clear direction." },
    supercar: { icon: "🏎️", label: "SUPER CAR",  color: "#f97316", bg: "rgba(249,115,22,0.10)",  border: "rgba(249,115,22,0.40)",  glow: "rgba(249,115,22,0.20)",  desc: "Aggressive momentum sweep — the speed of this order is the signal. Fast money moving fast." },
    bigbag:   { icon: "💰", label: "BIG BAG",    color: "#4ade80", bg: "rgba(74,222,128,0.10)",  border: "rgba(74,222,128,0.40)",  glow: "rgba(74,222,128,0.18)",  desc: "Fast day-trade setup — open and close same session. High volume, near-term catalyst. Uses 1 day trade." },
  };
  const TIER_RANK = { diamond: 4, gold: 3, supercar: 2, bigbag: 1 };

  function classifyFlowTier(ev) {
    const premium = ev.premium || 0;
    const volume  = Math.max(1, ev.volume || ev.callVolume || 0);
    // Diamond: massive sweep ≥ $500K OR any event ≥ $1M premium
    if (premium >= 1_000_000) return "diamond";
    if (ev.type === "sweep" && premium >= 500_000) return "diamond";
    // Gold: meaningful sweep ≥ $200K OR large volume spike ≥ $500K OR 5K+ contracts
    if (ev.type === "sweep" && premium >= 200_000) return "gold";
    if (premium >= 500_000) return "gold";
    if (volume >= 5_000) return "gold";
    // Super Car: fast aggressive sweep with high urgency volume
    if (ev.type === "sweep" && volume >= 2_000) return "supercar";
    if (ev.type === "volume_spike" && volume >= 3_000) return "supercar";
    // Big Bag: solid day-trade setup — directional sweep or spike with decent size
    if (ev.type === "sweep" && premium >= 100_000) return "bigbag";
    if (volume >= 1_000 && premium >= 50_000) return "bigbag";
    return null;
  }

  function eventDirection(ev) {
    if (ev.type === "cp_ratio") {
      if (ev.bias === "CALL HEAVY") return "bullish";
      if (ev.bias === "PUT HEAVY") return "bearish";
      return "neutral";
    }
    const side = (ev.optionType || "").toUpperCase();
    if (side === "CALL") return "bullish";
    if (side === "PUT") return "bearish";
    return "neutral";
  }

  // The single highest-tier live event, pulled out as a featured card so it
  // doesn't get lost in the grid — real classifyFlowTier, just re-sorted.
  let featuredEvent = null, featuredTier = null;
  for (const ev of flowEvents) {
    const t = classifyFlowTier(ev);
    if (!t) continue;
    if (!featuredTier || TIER_RANK[t] > TIER_RANK[featuredTier]) { featuredTier = t; featuredEvent = ev; }
  }

  // Pin the selected card to the top so new incoming events never push it away
  const logicalKey = (ev) => `${ev.ts || 0}_${ev.ticker}_${ev.type || ""}`;
  let feedEvents = flowEvents.filter(ev => ev !== featuredEvent);
  if (typeFilter !== "all") feedEvents = feedEvents.filter(ev => ev.type === typeFilter);
  if (dirFilter !== "all") feedEvents = feedEvents.filter(ev => eventDirection(ev) === dirFilter);
  const displayEvents = flowPopup
    ? [
        ...feedEvents.filter(ev => logicalKey(ev) === flowPopup),
        ...feedEvents.filter(ev => logicalKey(ev) !== flowPopup),
      ]
    : feedEvents;

  function renderEventCard(ev, reactKey, { featured } = {}) {
    const eKey = logicalKey(ev);
    const meta = TYPE_META[ev.type] || { label: ev.type?.toUpperCase() || "EVENT", color: "var(--muted)", bg: "var(--layer2)", border: "var(--border)" };
    const side = (ev.optionType || "").toUpperCase();
    const isCall = side === "CALL";
    const isPut = side === "PUT";
    const sideColor = isCall ? "#4ade80" : isPut ? "#f87171" : "var(--muted)";
    const vol = ev.volume?.toLocaleString() || ev.callVolume?.toLocaleString();
    const prem = ev.premium ? "$" + (ev.premium >= 1e6 ? (ev.premium / 1e6).toFixed(1) + "M" : (ev.premium / 1000).toFixed(0) + "K") : null;
    const isSelected = flowPopup === eKey;
    const tier = classifyFlowTier(ev);
    const tierMeta = tier ? TIER_META[tier] : null;
    // Default border/bg uses tier styling when not selected
    const restBg     = tierMeta ? tierMeta.bg     : "var(--layer2)";
    const restBorder = tierMeta ? tierMeta.border : "var(--border)";
    const restShadow = tierMeta ? `0 0 18px ${tierMeta.glow}` : "none";

    return (
      <div key={reactKey}
        className={`flow-card${featured ? " featured" : ""}`}
        onClick={() => setFlowPopup(isSelected ? null : eKey)}
        style={{
          background: isSelected ? meta.bg : restBg,
          border: `1px solid ${isSelected ? meta.border : restBorder}`,
          boxShadow: isSelected ? `0 0 20px ${meta.border}` : restShadow,
        }}
        onMouseEnter={e => { if (!isSelected) { e.currentTarget.style.borderColor = meta.border; e.currentTarget.style.background = meta.bg; } }}
        onMouseLeave={e => { if (!isSelected) { e.currentTarget.style.borderColor = restBorder; e.currentTarget.style.background = restBg; } }}
      >
        {/* Card header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 20,
              background: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}>
              {meta.label}
            </span>
            {side && (
              <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 20,
                background: isCall ? "rgba(74,222,128,0.1)" : "rgba(248,113,113,0.1)",
                color: sideColor, border: `1px solid ${sideColor}40` }}>
                {side}
              </span>
            )}
            {tierMeta && (
              <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 20,
                background: tierMeta.bg, color: tierMeta.color, border: `1px solid ${tierMeta.border}`,
                letterSpacing: 0.4 }}>
                {tierMeta.icon} {tierMeta.label}
              </span>
            )}
          </div>
          <span style={{ fontSize: 9, color: "var(--dim)" }}>
            {ev.ts ? new Date(ev.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
          </span>
        </div>

        {/* Ticker + strike */}
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: featured ? 22 : 18, fontWeight: 900, color: "var(--text)" }}>{ev.ticker}</span>
          {ev.strike && <span style={{ fontSize: 13, fontWeight: 700, color: sideColor }}>${ev.strike}</span>}
          {ev.bias && (
            <span style={{ fontSize: 9, fontWeight: 700, marginLeft: "auto",
              color: ev.bias === "CALL HEAVY" ? "#4ade80" : ev.bias === "PUT HEAVY" ? "#f87171" : "var(--muted)" }}>
              {ev.bias}
            </span>
          )}
        </div>

        {/* Stats row */}
        <div style={{ display: "flex", gap: 12, fontSize: 10 }}>
          {vol && (
            <div>
              <div style={{ color: "var(--dim)", fontSize: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Volume</div>
              <div style={{ fontWeight: 700, color: "var(--text)" }}>{vol}</div>
            </div>
          )}
          {prem && (
            <div>
              <div style={{ color: "var(--dim)", fontSize: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Premium</div>
              <div style={{ fontWeight: 700, color: "#4ade80" }}>{prem}</div>
            </div>
          )}
          {ev.ratio && (
            <div>
              <div style={{ color: "var(--dim)", fontSize: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>C/P Ratio</div>
              <div style={{ fontWeight: 700, color: "var(--text)" }}>{ev.ratio}x</div>
            </div>
          )}
        </div>

        {/* Tap hint when collapsed */}
        {!isSelected && (
          <div style={{ marginTop: 8, fontSize: 9, color: "var(--dim)", display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ opacity: 0.6 }}>↓</span> Tap to decode this trade
          </div>
        )}

        {/* ── POPUP EXPLANATION ── */}
        {isSelected && (() => {
          const ex = explainFlowEvent(ev);
          const sigColor = ex.signal === "Bullish" ? "#4ade80" : ex.signal === "Bearish" ? "#f87171" : "#fbbf24";
          return (
            <div style={{ marginTop: 12, borderTop: `1px solid ${meta.border}`, paddingTop: 12 }}>
              {/* Headline */}
              <div style={{ fontSize: 12, fontWeight: 800, color: meta.color, marginBottom: 8, lineHeight: 1.4 }}>
                {ex.headline}
              </div>

              {/* Signal badge */}
              <div style={{ display: "inline-flex", alignItems: "center", gap: 5, marginBottom: 10,
                padding: "3px 10px", borderRadius: 20,
                background: sigColor + "15", border: `1px solid ${sigColor}40`,
                fontSize: 10, fontWeight: 700, color: sigColor }}>
                Signal: {ex.signal}
              </div>

              {/* What section */}
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4 }}>What this means</div>
                <div style={{ fontSize: 11, color: "var(--text)", lineHeight: 1.6 }}>{ex.what}</div>
              </div>

              {/* Detail section */}
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4 }}>The trade</div>
                <div style={{ fontSize: 11, color: "var(--text)", lineHeight: 1.6 }}>{ex.detail}</div>
              </div>

              {/* Watch */}
              <div style={{ padding: "8px 10px", borderRadius: 8,
                background: "rgba(167,139,250,0.06)", border: "1px solid rgba(167,139,250,0.2)" }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: "#a78bfa", letterSpacing: 0.8, marginBottom: 3 }}>OMEGA WATCH</div>
                <div style={{ fontSize: 11, color: "var(--text)", lineHeight: 1.5 }}>{ex.watch}</div>
              </div>

              {/* Tier badge explanation — shown only when the event has a tier */}
              {tierMeta && (
                <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 8,
                  background: tierMeta.bg, border: `1px solid ${tierMeta.border}` }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: tierMeta.color, letterSpacing: 0.8, marginBottom: 3 }}>
                    {tierMeta.icon} {tierMeta.label} TRADE
                    {tier === "bigbag" && <span style={{ marginLeft: 8, fontSize: 8, background: "rgba(0,0,0,0.3)", padding: "1px 6px", borderRadius: 10 }}>⚡ USES 1 DAY TRADE</span>}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text)", lineHeight: 1.5 }}>{tierMeta.desc}</div>
                </div>
              )}

              {/* Buy Now — only when there's a clear direction (CALL or PUT) */}
              {side && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const rawPremium = ev.premium || 0;
                    const rawVolume = Math.max(1, ev.volume || ev.callVolume || 1);
                    const estimatedMid = rawPremium > 0 ? +(rawPremium / (rawVolume * 100)).toFixed(2) : 0;
                    setFlowBuyTrade({
                      ticker: ev.ticker,
                      symbol: ev.ticker,
                      strike: ev.strike,
                      optionType: (ev.optionType || "call").toLowerCase(),
                      mid: estimatedMid,
                      ask: estimatedMid,
                      kelly: { contracts: 1 },
                      ociGate: { confidence: 0.6 },
                    });
                  }}
                  style={{
                    display: "block",
                    marginTop: 10,
                    width: "100%",
                    padding: "11px 0",
                    borderRadius: 10,
                    border: "none",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 800,
                    letterSpacing: 0.6,
                    background: isCall
                      ? "linear-gradient(135deg,#4ade80 0%,#22c55e 100%)"
                      : isPut
                      ? "linear-gradient(135deg,#f87171 0%,#ef4444 100%)"
                      : "linear-gradient(135deg,#a78bfa 0%,#7c3aed 100%)",
                    color: "#fff",
                    boxShadow: isCall
                      ? "0 4px 14px rgba(74,222,128,0.4)"
                      : isPut
                      ? "0 4px 14px rgba(248,113,113,0.4)"
                      : "0 4px 14px rgba(167,139,250,0.4)",
                    fontFamily: "inherit",
                  }}
                >
                  BUY NOW — {ev.ticker}{ev.strike ? ` $${ev.strike}` : ""} {side}
                </button>
              )}

              <div style={{ marginTop: 8, fontSize: 9, color: "var(--dim)", textAlign: "center" }}>↑ Tap again to collapse</div>
            </div>
          );
        })()}
      </div>
    );
  }

  const bull = flowStats?.sentiment?.bullish || 0;
  const bear = flowStats?.sentiment?.bearish || 0;
  const sentTotal = bull + bear;
  const bullPct = sentTotal > 0 ? Math.round((bull / sentTotal) * 100) : 50;

  const TYPE_CHIPS = [
    { k: "all", label: "All" },
    { k: "sweep", label: "Sweeps" },
    { k: "volume_spike", label: "Vol Spike" },
    { k: "cp_ratio", label: "C/P Ratio" },
  ];

  return (
    <div className="flow-root" data-testid="flow-panel">
      <div className="src">Public.com chain polling — real-time flow + congressional whale tracking</div>
      <div className="stabs" style={{ marginBottom: 10 }}>
        <button className={`stab ${flowTab === "flow" ? "active" : ""}`} onClick={() => setFlowTab("flow")}>Options Flow</button>
        <button className={`stab ${flowTab === "whales" ? "active" : ""}`} onClick={() => setFlowTab("whales")}>Whale Tracking</button>
      </div>

      {flowTab === "flow" && (
        <>
          <div className="flow-topline">
            <span className={`tag ${connected ? "t-bull" : "t-neu"}`} data-testid="flow-status">{connected ? "● Live" : "Connecting..."}</span>
            <span className="flow-count">{flowEvents.length} events · click any card to decode</span>
          </div>

          {/* Stat strip */}
          {flowStats && (
            <div className="flow-stats">
              <div className="flow-stat"><span className="flow-stat-l">Sweeps</span><span className="flow-stat-v" style={{ color: "#a78bfa" }}>{flowStats.sweepCount || 0}</span></div>
              <div className="flow-stat"><span className="flow-stat-l">1h Events</span><span className="flow-stat-v" style={{ color: "#60a5fa" }}>{flowStats.recentCount || 0}</span></div>
              <div className="flow-stat"><span className="flow-stat-l">Premium</span><span className="flow-stat-v" style={{ color: "#fbbf24" }}>{flowStats.totalPremium >= 1e6 ? `$${(flowStats.totalPremium/1e6).toFixed(1)}M` : flowStats.totalPremium >= 1e3 ? `$${Math.round(flowStats.totalPremium/1000)}K` : `$${flowStats.totalPremium || 0}`}</span></div>
              {flowStats.topTickers?.length > 0 && (
                <div className="flow-hot">
                  <span className="flow-hot-l">Hot</span>
                  {flowStats.topTickers.slice(0, 3).map(t => (
                    <span key={t.ticker} className="flow-hot-t">{t.ticker}<span className="flow-hot-n">{t.events}</span></span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Sentiment bar — real bullish/bearish counts, drawn as one proportion instead of two separate pills */}
          {sentTotal > 0 && (
            <div className="flow-sentiment">
              <div className="flow-sentiment-labels">
                <span style={{ color: "var(--green)" }}>{bullPct}% Bullish · {bull}</span>
                <span style={{ color: "var(--red)" }}>{100 - bullPct}% Bearish · {bear}</span>
              </div>
              <div className="flow-sentiment-bar">
                <div className="flow-sentiment-bull" style={{ width: bullPct + "%" }} />
                <div className="flow-sentiment-bear" style={{ width: (100 - bullPct) + "%" }} />
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flow-filters">
            <div className="flow-chipgroup">
              {TYPE_CHIPS.map(c => (
                <button key={c.k} className={`flow-chip ${typeFilter === c.k ? "on" : ""}`} onClick={() => setTypeFilter(c.k)}>{c.label}</button>
              ))}
            </div>
            <div className="flow-chipgroup">
              {[["all", "All"], ["bullish", "Bullish"], ["bearish", "Bearish"]].map(([k, label]) => (
                <button key={k} className={`flow-chip ${dirFilter === k ? "on" : ""}`} onClick={() => setDirFilter(k)}>{label}</button>
              ))}
            </div>
          </div>

          {flowEvents.length === 0 && (
            <div className="card" style={{ textAlign: "center", padding: "32px 20px", color: "var(--muted)", fontSize: 12 }}>
              Scanning Public.com chains… flow events will appear here.
            </div>
          )}

          {/* Featured — the single highest-tier live event */}
          {featuredEvent && (
            <div className="flow-featured">
              <div className="flow-featured-label">{TIER_META[featuredTier].icon} TOP SIGNAL RIGHT NOW</div>
              {renderEventCard(featuredEvent, "featured", { featured: true })}
            </div>
          )}

          {/* Flow event grid */}
          {feedEvents.length === 0 && flowEvents.length > 0 ? (
            <div className="card" style={{ textAlign: "center", padding: "24px 20px", color: "var(--muted)", fontSize: 12 }}>
              No events match this filter right now.
            </div>
          ) : (
            <div className="flow-grid">
              {displayEvents.map((ev, i) => renderEventCard(ev, `${logicalKey(ev)}__${i}`))}
            </div>
          )}
        </>
      )}

      {flowTab === "whales" && (
        <div>
          <div className="card" style={{ borderColor: "rgba(167,139,250,0.3)", background: "rgba(167,139,250,0.04)" }}>
            <div className="row" style={{ marginBottom: 8 }}>
              <div className="sh" style={{ display: "flex", alignItems: "center", gap: 6, margin: 0 }}>
                Congressional Whale Tracking
                <span className="tag t-pur" style={{ fontSize: 8 }}>Capitol Trades</span>
                {whaleMeta?.source === "ai_fallback" && <span className="tag t-neu" style={{ fontSize: 7 }}>AI FALLBACK</span>}
              </div>
            </div>
            <div className="flow-whale-toolbar">
              <input
                className="flow-whale-input" placeholder="AAPL" value={whaleSymbol}
                onChange={e => setWhaleSymbol(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === "Enter" && loadWhales(whaleSymbol)}
              />
              <button className="btn btn-blue" onClick={() => loadWhales(whaleSymbol)} disabled={whaleLoading} style={{ fontSize: 11 }}>
                {whaleLoading ? "Loading..." : whaleSymbol ? `Filter ${whaleSymbol}` : "Refresh"}
              </button>
              {whaleSymbol && <button className="btn" onClick={() => { setWhaleSymbol(""); loadWhales(""); }} style={{ fontSize: 10 }}>Clear</button>}
            </div>
            <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 8 }}>
              Notable stock trades by members of Congress — follow the smart money.
            </div>
            {whaleMeta?.fetchedAt && (
              <div style={{ fontSize: 9, color: "var(--dim)", marginBottom: 10 }}>
                Updated: {new Date(whaleMeta.fetchedAt).toLocaleString()} · Source: {whaleMeta.source} · {whaleMeta.symbol !== "ALL" ? `Filtered: ${whaleMeta.symbol}` : "All tickers"}
              </div>
            )}

            {whaleLoading && <div style={{ color: "var(--muted)", fontSize: 11, padding: 12 }}>Loading congressional trades...</div>}
            {!whaleLoading && whales.length === 0 && (
              <div style={{ color: "var(--dim)", fontSize: 11, padding: 12 }}>
                No trades found{whaleSymbol ? ` for ${whaleSymbol}` : ""}. Capitol Trades may be rate-limiting or data is cached.
              </div>
            )}
            {!whaleLoading && whales.length > 0 && (
              <div className="flow-whale-list">
                {whales.map((w, i) => {
                  const isBuy = w.type?.toLowerCase().includes("buy") || w.type?.toLowerCase().includes("purchase");
                  return (
                    <div className="flow-whale-card" key={i}>
                      <div className="flow-whale-accent" style={{ background: isBuy ? "var(--green)" : "var(--red)" }} />
                      <div className="flow-whale-main">
                        <div className="flow-whale-top">
                          <span className="flow-whale-name">{w.politician}</span>
                          <span className="tag t-blue">{w.ticker}</span>
                        </div>
                        <div className="flow-whale-bottom">
                          <span className={`tag ${isBuy ? "t-bull" : "t-bear"}`}>{w.type}</span>
                          <span className="flow-whale-size" style={{ color: isBuy ? "var(--green)" : "var(--red)" }}>{w.size}</span>
                          <span className="flow-whale-date">{w.dateFiled}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {flowBuyTrade && (
        <RealTradePreviewModal trade={flowBuyTrade} onClose={() => setFlowBuyTrade(null)} />
      )}
    </div>
  );
}

export default FlowPanel;
